import * as React from "react";
import {
  styled,
  Typography,
  SxProps,
  Stack,
  Box,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Button,
} from "@mui/material";

import SmallAccordion from "../../Misc/SmallAccordion";
import ConfigContentRecaps from "../../Config/ConfigContentRecaps/config-content-row-recap";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";
import DownloadIcon from "@mui/icons-material/Download";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BuildCircleOutlinedIcon from "@mui/icons-material/BuildCircleOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";

export type ConfigCardProps = {
  className?: string;
  sx?: SxProps;

  title: string;
  status: "complete" | "error" | "todo" | "warning";
  helperText?: string;
  configType: string;

  content: {
    quality: string;
    voice: string;
    collection: string;
    subtitles: string;
    transfert: string;
  };

  onExpand: () => void;
  expanded: boolean;
  forms: any;
};

const ConfigCard: React.FC<ConfigCardProps> = (props) => {
  const { t } = useTranslation();
  const { expanded, onExpand, title, status, helperText, configType, content, forms, ...otherProps } = props;

  const onClickAction: React.MouseEventHandler<HTMLButtonElement> = React.useCallback((e) => {
    e.stopPropagation();
  }, []);

  return (
    <Box {...otherProps}>
      <CardAccordion expanded={expanded}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon fontSize="small" />}
          aria-controls="config-content"
          id="config-header"
          onClick={onExpand}
        >
          <Stack direction="row" flex={1} width="100%" mb={1}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flex={1} mr={2}>
              <AccordionTitle direction="row" alignItems="center">
                {/* TODO : replace with proper icon : AssetIcon or OrderIcon ? => then change props to asset or order enum
                <AssetIcon type={configType} /> */}
                <Typography variant="subtitle1" component="h2" fontWeight={600}>
                  {title}
                </Typography>
              </AccordionTitle>
              {status === "complete" && (
                <Chip label={t("common:complete")} color="success" size="small" icon={<CheckIcon />} />
              )}
              {(status === "warning" || status === "todo") && (
                <Chip label={helperText || ""} color="warning" size="small" icon={<BuildCircleOutlinedIcon />} />
              )}
              {status === "error" && (
                <Chip label={helperText || ""} color="error" size="small" icon={<InfoOutlinedIcon />} />
              )}
            </Stack>
            <Stack direction="row" spacing={0.5}>
              <Button variant="icon" size="small" onClick={onClickAction}>
                <DownloadIcon />
              </Button>
              <Button variant="icon" size="small" onClick={onClickAction}>
                <ContentCopyIcon />
              </Button>
              <Button variant="icon" size="small" onClick={onClickAction}>
                <DeleteOutlineIcon color="error" />
              </Button>
            </Stack>
          </Stack>
          <Box className="config-card-short-content" sx={{ color: "text.secondary" }}>
            <ConfigContentRecaps {...content} />
          </Box>
        </AccordionSummary>
        {/*<AccordionDetails className="card-config-accordion-details">{configForm}</AccordionDetails>*/}
      </CardAccordion>
    </Box>
  );
};

const CardAccordion = styled(SmallAccordion)(({ theme }) => ({
  position: "relative",
  transition: "all .3s",
  border: `1px solid ${theme.palette.backgroundAlpha.border}`,
  background: "none",
  "> .MuiAccordionSummary-root": {
    paddingRight: theme.spacing(1),
    alignItems: "flex-start",
    ".MuiAccordionSummary-expandIconWrapper": {
      order: 1,
      marginTop: theme.spacing(2),
      marginRight: theme.spacing(1.5),
      transform: "rotate(-90deg)",
    },
    ".MuiAccordionSummary-content": {
      order: 2,
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  ".card-config-accordion-details": {
    border: "none",
    paddingTop: 0,
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(2),
    paddingBottom: theme.spacing(3),
  },

  "&:hover": {
    background: theme.palette.backgroundAlpha.hover,
  },

  "&.Mui-expanded": {
    "&:hover": {
      background: "transparent",
    },
    "&:after": {
      border: `3px solid ${theme.palette.backgroundAlpha.border}`,
    },
    ".config-card-short-content": {
      display: "none",
    },
    ".MuiAccordionSummary-expandIconWrapper": {
      transform: "rotate(0deg)",
    },
  },
  ".config-form-section": {
    ".MuiAccordion-root": {
      background: "transparent",
    },
  },
}));

const AccordionTitle = styled(Stack)(({ theme }) => ({
  svg: {
    color: theme.palette.info.main,
    marginRight: theme.spacing(1.5),
  },
}));

export default ConfigCard;
